<template>
    <main class="occupation-page main-section">
        <div class="occupation-card">
            <header class="occupation-header">
                <div>
                    <router-link to="/dashboard" class="occupation-back">← Poste de soins</router-link>
                    <h1>Occupation des lits</h1>
                    <p>Situation actuelle par chambre. Un lit en nettoyage ou dont l’état est incohérent n’est pas disponible.</p>
                </div>
                <button type="button" class="occupation-refresh" :disabled="loading" @click="load">Actualiser</button>
            </header>

            <p v-if="error" class="occupation-error" role="alert">{{ error }}</p>
            <div class="occupation-summary" aria-label="Résumé des lits">
                <div><strong>{{ counts.total }}</strong><span>Total</span></div>
                <div class="is-free"><strong>{{ counts.free }}</strong><span>Libres</span></div>
                <div><strong>{{ counts.occupied }}</strong><span>Occupés</span></div>
                <div><strong>{{ counts.cleaning }}</strong><span>À nettoyer</span></div>
                <div><strong>{{ counts.unavailable }}</strong><span>Indisponibles</span></div>
            </div>

            <div class="occupation-filters">
                <input v-model.trim="search" type="search" aria-label="Rechercher une chambre, un lit ou un patient" placeholder="Rechercher une chambre, un lit ou un patient">
                <select v-model="status" aria-label="Filtrer par disponibilité">
                    <option value="all">Tous les lits</option>
                    <option value="free">Libres</option>
                    <option value="occupied">Occupés</option>
                    <option value="cleaning">À nettoyer</option>
                    <option value="unavailable">Indisponibles</option>
                </select>
            </div>

            <p v-if="loading && !beds.length" class="occupation-empty">Chargement de l’occupation…</p>
            <p v-else-if="!filteredBeds.length" class="occupation-empty">Aucun lit ne correspond aux filtres.</p>
            <div v-else class="occupation-table-wrap">
                <table class="occupation-table">
                    <thead><tr><th scope="col">Chambre</th><th scope="col">Lit</th><th scope="col">État</th><th scope="col">Patient</th><th scope="col">Service</th><th scope="col">Installé le</th></tr></thead>
                    <tbody><tr v-for="bed in filteredBeds" :key="bed.id">
                        <td>{{ bed.chambre }}</td><td>{{ bed.lit }}</td>
                        <td><span class="occupation-badge" :class="'state-' + bed.status">{{ statusLabel(bed.status) }}</span></td>
                        <td>{{ bed.patient || '—' }}</td><td>{{ bed.service_name || '—' }}</td>
                        <td>{{ formatDate(bed.installed_at) }}</td>
                    </tr></tbody>
                </table>
            </div>
            <p class="occupation-count">{{ filteredBeds.length }} lit{{ filteredBeds.length > 1 ? 's' : '' }} affiché{{ filteredBeds.length > 1 ? 's' : '' }}</p>
        </div>
    </main>
</template>

<script>
import stationApi from '@/api/consultations/_nursing-station'
import { parseBackendTimestamp } from '@/utils/backend-timestamp'

export default {
    name: 'occupation-lits',
    data() {
        return { beds: [], counts: { total: 0, free: 0, occupied: 0, cleaning: 0, unavailable: 0 },
            search: '', status: 'all', loading: false, error: '' }
    },
    computed: {
        filteredBeds() {
            const term = this.search.toLocaleLowerCase('fr').trim()
            return this.beds.filter(bed =>
                (this.status === 'all' || bed.status === this.status) &&
                (!term || [bed.chambre, bed.lit, bed.patient, bed.service_name]
                    .filter(Boolean).join(' ').toLocaleLowerCase('fr').includes(term)))
        }
    },
    mounted() { this.load() },
    methods: {
        statusLabel(status) {
            return { free: 'Libre', occupied: 'Occupé', cleaning: 'À nettoyer', unavailable: 'Indisponible' }[status] || 'Indisponible'
        },
        formatDate(value) {
            if (!value) return '—'
            const date = parseBackendTimestamp(value)
            return isNaN(date.getTime()) ? '—' : date.toLocaleString('fr-FR', { timeZone: this.getClinicTimezone() })
        },
        async load() {
            this.loading = true; this.error = ''
            try {
                const response = await stationApi.bedOccupancy()
                if (!response || !response.success) throw new Error('Occupation indisponible')
                this.beds = response.data || []
                this.counts = response.counts || this.counts
            } catch (error) { this.error = 'Impossible de charger l’occupation des lits. Réessayez.' }
            finally { this.loading = false }
        }
    }
}
</script>

<style scoped lang="scss">
.occupation-page { padding: 24px; }
.occupation-card { background: #fff; border-radius: 22px; padding: 30px; color: var(--ui-text, #34415f); }
.occupation-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; }
.occupation-header h1 { font-size: 30px; margin: 8px 0; }
.occupation-header p, .occupation-count { color: var(--ui-text-secondary, #73819d); }
.occupation-back { color: #4375ed; font-weight: 600; }
.occupation-refresh { border: 1px solid #c9d8f5; border-radius: 10px; padding: 10px 18px; color: #3c6fdf; background: #f6f9ff; }
.occupation-summary { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 12px; margin: 28px 0 22px; }
.occupation-summary > div { border: 1px solid #dce5f5; border-radius: 14px; background: #f8faff; padding: 15px; display: flex; flex-direction: column; gap: 2px; }
.occupation-summary strong { font-size: 27px; }.occupation-summary span { color: var(--ui-text-secondary, #74829c); }
.occupation-summary .is-free { background: #edf7f0; border-color: #c9e6d2; }
.occupation-filters { display: flex; gap: 12px; margin-bottom: 20px; }
.occupation-filters input, .occupation-filters select { border: 1px solid #cfdbef; border-radius: 10px; padding: 11px 14px; color: var(--ui-text, #34415f); background: #fff; }
.occupation-filters input { flex: 1; }.occupation-filters select { min-width: 190px; }
.occupation-table-wrap { overflow-x: auto; }.occupation-table { width: 100%; border-collapse: collapse; text-align: left; }
.occupation-table th { background: #f2f5fb; color: var(--ui-text-secondary, #667791); }.occupation-table td, .occupation-table th { padding: 13px 16px; border-bottom: 1px solid #e5ebf5; text-align: left; }
.occupation-badge { display: inline-block; border-radius: 12px; padding: 4px 10px; background: #e8edf5; white-space: nowrap; }
.state-free { background: #e5f5e9; color: #31764b; }.state-occupied { background: #eaf0ff; color: #315ebd; }
.state-cleaning { background: #fff0d9; color: #976512; }.state-unavailable { background: #f5e9e9; color: #ab5454; }
.occupation-empty, .occupation-error { padding: 24px 0; }.occupation-error { color: #b44e4e; }
@media (max-width: 850px) { .occupation-page { padding: 12px; } .occupation-card { padding: 20px; } .occupation-summary { grid-template-columns: repeat(2, minmax(0, 1fr)); } .occupation-filters { flex-direction: column; } }
</style>
